/**
 * @file prefsBackend.ts
 *
 * A host that can store preferences outside the browser provides one through
 * the `prefs` section of its host registration (see host.ts; e.g.
 * grabcadPlugin.ts).
 *
 * IndexedDB stays the place prefs are read from: it's local and instant, so a
 * warm start costs nothing. A backend is the copy that survives the browser
 * storage being wiped — GrabCAD Print clears IndexedDB between launches, so a
 * "don't show again" flag ticked in one session would otherwise be forgotten by
 * the next.
 *
 * Both methods work on the whole pref set rather than one key. Hosts tend to
 * charge a round-trip per call, and the set is a handful of short strings, so
 * one blob keeps startup to a single request no matter how many prefs exist.
 *
 * See db.ts's prefs section for how these are used.
 */

import { getHost, registerHost } from './host.ts';

export interface PrefsBackend {
  /**
   * Every pref the host is holding. Null means it couldn't answer — treated as
   * "don't know", so nothing local is touched. An empty object is a real
   * answer: the host has nothing.
   */
  loadAll(): Promise<Record<string, string> | null>;
  /**
   * Hands the host the full pref set to store. Called in the background, so it
   * need not resolve quickly — nothing waits on it.
   */
  saveAll(prefs: Record<string, string>): Promise<void>;
}

/** Same as `registerHost(env, { prefs: backend })`. */
export function registerPrefsBackend(env: string, backend: PrefsBackend): void {
  registerHost(env, { prefs: backend });
}

/** The backend for the given env, or undefined when that env has none (in which
 *  case IndexedDB is the whole story). */
export function getPrefsBackend(env: string | null): PrefsBackend | undefined {
  return getHost(env)?.prefs;
}
